import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { NearbyChurches } from './NearbyChurches';
import { api } from '../../shared/api/client';
import type { ChurchSearchResult } from '../../shared/api/types';
import { EmptyState, Logo } from '../../shared/ui/components';
import { countryName } from '../../shared/geo/places';
import { useSession } from '../session/SessionProvider';

/** Portal público: «Buscar mi iglesia» por nombre, ciudad, estado o código. */
export function FindChurchPage() {
  const [params] = useSearchParams();
  const signedIn = useSession().state.status === 'authenticated';
  const [q, setQ] = useState('');
  const [items, setItems] = useState<ChurchSearchResult[] | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api.get<{ items: ChurchSearchResult[] }>(`/multichurch/public/churches?q=${encodeURIComponent(q.trim())}`, ctrl.signal).then((r) => setItems(r.items)).catch(() => undefined);
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);
  return (
    <div className="landing">
      <header className="landing__bar">
        <Link to="/" aria-label="KEORA">
          <Logo />
        </Link>
        <nav className="landing__nav" aria-label="Acceso">
          {signedIn ? (
            <Link className="btn btn--ghost" to="/app">
              Volver a mi inicio
            </Link>
          ) : (
            <Link className="btn btn--ghost" to="/entrar">
              Iniciar sesión
            </Link>
          )}
        </nav>
      </header>
      <main className="public">
        <header className="page__header">
          <h1>Buscar mi iglesia</h1>
          <p className="muted">Busca por nombre, ciudad, estado o código de la iglesia, o toca «Cerca de mí» para ver las más cercanas. Desde su página pides unirte.</p>
        </header>
        <NearbyChurches auto={params.get('cerca') === '1'} />
        <label className="visually-hidden" htmlFor="find-q">
          Buscar iglesia
        </label>
        <input id="find-q" className="find-input" type="search" autoFocus={params.get('cerca') !== '1'} placeholder="Ej. Durango, Tabernáculo…" value={q} onChange={(e) => setQ(e.target.value)} />
        {items && items.length === 0 ? <EmptyState title="No encontramos iglesias con esa búsqueda." /> : null}
        <ul className="church-results">
          {(items ?? []).map((c) => (
            <li key={c.slug}>
              <Link to={`/i/${c.slug}`} className="church-result">
                {c.logoUrl ? <img src={c.logoUrl} alt="" /> : <span className="church-result__initial" aria-hidden="true">{c.name.charAt(0)}</span>}
                <span>
                  <strong>{c.name}</strong>
                  <span className="muted small block">{[c.city, c.state, c.country ? countryName(c.country) : null].filter(Boolean).join(', ') || 'Ubicación sin registrar'}</span>
                  {c.tagline ? <span className="small block">{c.tagline}</span> : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
